import { useState, useEffect, useContext, useRef } from "react";
import { ChartContext } from "../Context/ChartContext/ChartContext";
import MUIGrid from "@mui/material/Grid";
import MUIBox from "@mui/material/Box";
import MUIIconButton from "@mui/material/IconButton";
import MUICloseIcon from "@mui/icons-material/Close";
import MUIZoomInIcon from "@mui/icons-material/ZoomIn";
import MUIDialog from "@mui/material/Dialog";
import MUIDialogContent from "@mui/material/DialogContent";
import CompletedPaperworkTransferList from "./CompletedPaperworkTransferList";
import Progress from "./Progress";
import api from "../Seating/Security/api";
import UploadedFile from "../Utility/Crud/UploadedFile";
/**
 * The PdfMergeEditor component.
 * @param {Object} props - The properties of the component.
 * @param {Array} props.items - The items to display in the TransferList component.
 * @returns {JSX.Element} The PdfMergeEditor component.
 */
export default function PdfMergeEditor(props) {
  const chartContext = useContext(ChartContext);
  const [checkedItem, setCheckedItem] = useState(null);
  const [leftItems, setLeftItems] = useState(props.items);
  const [rightItems, setRightItems] = useState([]);
  const [downloadedFiles, setDownloadedFiles] = useState({});

  const deletedPageIdsRef = useRef(new Set());

  const [thumbnailLoading, setThumbnailLoading] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);

  /**
   * Downloads the files from the rightItems and sets the downloadedFiles state.
   */
  useEffect(() => {
    const downloadFiles = async () => {
      const rightItemIds = new Set(rightItems.map((item) => item.id));

      // Remove pages from downloadedFiles that are no longer in rightItems
      setDownloadedFiles((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((pageKey) => {
          const fileId = updated[pageKey].fileId;
          if (!rightItemIds.has(fileId)) {
            delete updated[pageKey];
          }
        });
        return updated;
      });

      // Download new files (only when we don't already have pages for this file)
      for (const item of rightItems) {
        const activityFile = Object.values(
          chartContext.chart.activityFile,
        ).find((activityFile) => activityFile.fileId === item.id);

        // For multi-page PDFs, keys are "fileId_pageIndex" - check if we have any pages from this file
        const hasPagesForFile = Object.keys(downloadedFiles).some((key) => {
          const keyFileId = parseInt(key.split("_")[0], 10);
          return keyFileId === item.id;
        });

        if (!hasPagesForFile) {
          setThumbnailLoading(true);
          try {
            api
              .send(
                "GET",
                `/deliveryPaperwork/image/${activityFile.fileId}`,
                null,
                2,
                true,
              )
              .then(async (res) => {
                if (res) {
                  // Add each page as a separate entry (skip pages user has deleted)
                  const newPages = res
                    .map((image, pageIndex) => ({
                      fileId: item.id,
                      pageIndex: pageIndex + 1,
                      selected: false,
                      data: image,
                    }))
                    .filter((page) => {
                      const pageKey = `${page.fileId}_${page.pageIndex}`;
                      return !deletedPageIdsRef.current.has(pageKey);
                    });
                  setDownloadedFiles((prev) => {
                    const updated = { ...prev };
                    newPages.forEach((page) => {
                      const pageKey = `${page.fileId}_${page.pageIndex}`;
                      updated[pageKey] = page;
                    });
                    return updated;
                  });
                  setThumbnailLoading(false);
                } else {
                  console.error("There was an issue downloading the file.");
                  setThumbnailLoading(false);
                }
                setThumbnailLoading(false);
              })
              .catch((err) => {
                console.log(err);
                setThumbnailLoading(false);
              });
          } catch (error) {
            console.error(`Failed to download file ${item.id}:`, error);
          }
        }
      }

      // Reorder downloadedFiles to match rightItems order
      setDownloadedFiles((prev) => {
        const reordered = {};
        rightItems.forEach((item) => {
          Object.keys(prev).forEach((pageKey) => {
            if (prev[pageKey].fileId === item.id) {
              reordered[pageKey] = prev[pageKey];
            }
          });
        });
        return reordered;
      });
    };

    if (rightItems.length > 0) {
      downloadFiles();
    } else {
      // Clear all downloaded files when rightItems is empty
      setDownloadedFiles({});
    }
  }, [rightItems]);

  /**
   * Prepares the merged file data and passes it to the parent component.
   */
  useEffect(() => {
    if (
      Object.keys(downloadedFiles).length > 0 &&
      props.prepareFile &&
      rightItems.length > 1
    ) {
      // Create an array of pages in order with their file IDs and page indices
      // Get unique file IDs in order
      const uniqueFileIds = [
        ...new Set(Object.values(downloadedFiles).map((page) => page.fileId)),
      ];

      // Create the upload array with page information
      const uploadPages = Object.entries(downloadedFiles).map(
        ([pageKey, page], index) => ({
          fileId: page.fileId,
          pageIndeces: page.pageIndex,
          pageIndex: index,
        }),
      );

      // Create the final object structure
      const orderedPages = {
        documents: uniqueFileIds,
        upload: uploadPages,
      };
      props.prepareFile(orderedPages);
    } else if (
      (Object.keys(downloadedFiles).length === 0 && props.prepareFile) ||
      rightItems.length <= 1
    ) {
      props.prepareFile(null);
    }
  }, [downloadedFiles]);

  /**
   * Handles deleting a page from the downloadedFiles state.
   * @param {string} pageId - The id of the page to delete.
   */
  const handleDeletePage = (pageId) => {
    // Extract fileId from pageId (format: "fileId_pageIndex")
    const fileId = parseInt(pageId.split("_")[0], 10);

    // Remove from downloadedFiles first
    setDownloadedFiles((prev) => {
      const updated = { ...prev };
      delete updated[pageId];

      // Check if there are any remaining pages from this file
      const remainingPagesFromFile = Object.keys(updated).some((key) => {
        return parseInt(key.split("_")[0]) === fileId;
      });

      // If no pages remain from this file, move it back to leftItems
      if (!remainingPagesFromFile) {
        const fileToRemove = rightItems.find((item) => item.id === fileId);

        if (fileToRemove) {
          // Remove the file from rightItems
          setRightItems((prevRight) =>
            prevRight.filter((item) => item.id !== fileId),
          );

          // Add it back to leftItems
          setLeftItems((prevLeft) =>
            UploadedFile.sort([...prevLeft, fileToRemove]),
          );
        }
      }

      return updated;
    });
  };

  /**
   * Renders the reorder pages component.
   * @param {string} pageId - The id of the page.
   * @param {string} src - The source of the page.
   * @returns {JSX.Element} The reorder pages component.
   */
  const renderReorderPages = (pageId, src) => {
    const fileId = pageId.split("_")[0];
    const uploadedFile = props.items.find(
      (item) => item.id === parseInt(fileId),
    );

    return (
      <MUIGrid item xs={1.5} key={pageId}>
        <MUIBox
          draggable
          sx={{
            width: "100%",
            height: 180,
            border: (theme) => `2px solid ${theme.palette.text.disabled}`,
            cursor: "grab",
            position: "relative",
            transition: "all 0.2s ease",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "grey.100",
            "&:active": {
              cursor: "grabbing",
            },
          }}
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", pageId);
            e.currentTarget.style.opacity = "0.5";
            e.currentTarget.style.cursor = "grabbing";
          }}
          onDragEnd={(e) => {
            e.currentTarget.style.opacity = "";
            e.currentTarget.style.cursor = "";
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow = "0 0 0 1px #2196f3";
            e.currentTarget.style.borderColor = "#2196f3";
            e.currentTarget.style.backgroundColor = "rgba(33, 150, 243, 0.1)";
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.currentTarget.style.transform = "";
            e.currentTarget.style.boxShadow = "";
            e.currentTarget.style.borderColor = "";
            e.currentTarget.style.backgroundColor = "";
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.style.transform = "";
            e.currentTarget.style.boxShadow = "";
            e.currentTarget.style.borderColor = "";
            e.currentTarget.style.backgroundColor = "";
            const draggedPageId = e.dataTransfer.getData("text/plain");
            if (draggedPageId === pageId) return;

            setDownloadedFiles((prevFiles) => {
              const entries = Object.entries(prevFiles);
              const draggedIndex = entries.findIndex(
                ([id]) => id === draggedPageId,
              );
              const targetIndex = entries.findIndex(([id]) => id === pageId);

              if (draggedIndex === -1 || targetIndex === -1) return prevFiles;

              const [draggedEntry] = entries.splice(draggedIndex, 1);
              entries.splice(targetIndex, 0, draggedEntry);

              return Object.fromEntries(entries);
            });
          }}
          onClick={() => props.handlePdfViewer(uploadedFile)}
        >
          <MUIIconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDeletePage(pageId);
            }}
            sx={{
              position: "absolute",
              top: 2,
              right: 2,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              color: "white",
              padding: "2px",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.error.main,
              },
              zIndex: 1,
            }}
          >
            <MUICloseIcon fontSize="small" />
          </MUIIconButton>
          <MUIIconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEnlargedImage(src);
            }}
            sx={{
              position: "absolute",
              top: 32,
              right: 2,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              color: "white",
              padding: "2px",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.secondary.main,
              },
              zIndex: 1,
            }}
          >
            <MUIZoomInIcon fontSize="small" />
          </MUIIconButton>
          <img
            src={src}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              pointerEvents: "none",
              display: "block",
            }}
          />
        </MUIBox>
      </MUIGrid>
    );
  };

  /**
   * Renders the TransferList component.
   * @returns {JSX.Element} The TransferList component.
   */
  return (
    <>
      <CompletedPaperworkTransferList
        leftItems={leftItems}
        rightItems={rightItems}
        checkedItem={checkedItem}
        setLeftItems={setLeftItems}
        setRightItems={setRightItems}
        setCheckedItem={setCheckedItem}
      />
      {thumbnailLoading ? (
        <MUIGrid container spacing={1} sx={{ mt: 2 }}>
          <MUIGrid item xs={12}>
            <Progress />
          </MUIGrid>
        </MUIGrid>
      ) : (
        <MUIGrid container spacing={1} sx={{ mt: 2 }}>
          {Object.entries(downloadedFiles).map(([pageId, pageData]) => {
            const src = "data:image/jpeg;base64, " + pageData.data;
            return renderReorderPages(pageId, src);
          })}
        </MUIGrid>
      )}

      {/* Enlarged Image Dialog */}
      <MUIDialog
        open={enlargedImage !== null}
        onClose={() => setEnlargedImage(null)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "visible",
          },
        }}
      >
        <MUIDialogContent
          sx={{
            p: 0,
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "visible",
          }}
        >
          {enlargedImage && (
            <img
              src={enlargedImage}
              alt="Enlarged preview"
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: "4px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              }}
            />
          )}
        </MUIDialogContent>
      </MUIDialog>
    </>
  );
}
